import React, { useState, useEffect, useMemo,useRef } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import { FaTimes, FaPaperclip, FaDownload, FaTrash, FaExpand, FaCompress, FaEye, FaBox, FaBarcode } from 'react-icons/fa';
import { useScreenPermissions } from '../components/useScreenPermissions';
import PurchaseOrderViewModal from './PurchaseOrderViewModal';
import SalesOrderViewModal from './SalesOrderViewModal';

// SVG Icons (same as PO modal)
const CloseIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const TrackDocumentIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const HidePanelIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

// Modern Error Alert Icon
const ErrorAlertIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// Document type icons
const SalesOrderIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const PurchaseOrderIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2l3.54 6.54-2.32 2.32L6 22h12l-1.22-11.14-2.32-2.32L18 2H6z"></path>
    <line x1="12" y1="22" x2="12" y2="16"></line>
    <line x1="9" y1="16" x2="15" y2="16"></line>
  </svg>
);

const MRNIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

// Modern Error Popup Component
const ErrorPopup = ({ message, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000); // Auto-close after 4 seconds
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop without blur */}
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose}></div>
      
      {/* Modern Popup Card */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full transform transition-all duration-300 animate-fadeIn">
        {/* Decorative top border */}
        <div className="h-1 w-full bg-gradient-to-r from-red-500 to-red-600 rounded-t-2xl"></div>
        
        <div className="p-6">
          <div className="flex items-start">
            {/* Icon with gradient background */}
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                <ErrorAlertIcon className="h-6 w-6 text-white" />
              </div>
            </div>
            
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-bold text-gray-900">Access Denied</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-600">{message}</p>
              </div>
            </div>
            
            {/* Close button */}
            <div className="ml-4 flex-shrink-0 flex">
              <button
                className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                onClick={onClose}
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 border border-transparent rounded-lg shadow-md hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all"
              onClick={onClose}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Improved Batch Info Modal Component
const BatchInfoModal = ({ isOpen, onClose, batches = [], productName = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <FaBox className="text-gray-700 text-sm" />
              </div>
              <h3 className="text-base font-medium text-gray-800">Batch Details</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex items-center">
            <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
            <span className="text-xs font-medium text-gray-800 ">{productName || "N/A"}</span>
          </div>
        </div>
        
        {/* Batch Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {batches.length > 0 ? (
                  batches.map((batch, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {batch.BatchNumber || batch.batchNumber || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-700">
                          {batch.ExpiryDate || batch.expiryDate 
                            ? dayjs(batch.ExpiryDate || batch.expiryDate).format('YYYY-MM-DD') 
                            : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800">
                          {batch.Quantity || batch.quantity || '0'}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <FaBox className="text-gray-300 text-xl mb-1" />
                        <span>No batch information available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {batches.length} batch{batches.length !== 1 ? 'es' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Improved Serial Info Modal Component
const SerialInfoModal = ({ isOpen, onClose, serialNumbers = [], productName = "", locationCode = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <FaBarcode className="text-gray-700 text-sm" />
              </div>
              <h3 className="text-base font-medium text-gray-800">Serial Numbers</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
              <span className="text-xs font-medium text-gray-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Location:</span>
              <span className="text-xs font-medium text-gray-800">{locationCode || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Count:</span>
              <span className="text-xs font-medium text-gray-800">{serialNumbers.length}</span>
            </div>
          </div>
        </div>
        
        {/* Serial Numbers Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {serialNumbers.length > 0 ? (
                  serialNumbers.map((serial, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-mono font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {serial}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                        {productName || "N/A"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {locationCode || "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <FaBarcode className="text-gray-300 text-xl mb-1" />
                        <span>No serial numbers available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {serialNumbers.length} serial number{serialNumbers.length !== 1 ? 's' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const API_URL = '/api';

// Common units of measurement
const UOM_OPTIONS = [
  { value: "PCS", label: "Pieces" },
  { value: "KG", label: "Kilograms" },
  { value: "L", label: "Liters" },
  { value: "PACK", label: "Packs" },
];

// Status options
const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

const MaterialReceiptViewModal = ({ open, onClose, mrnId }) => {
  const [mrnDetails, setMrnDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    vendors: [],
    locations: [],
  });
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Tracking state
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [showTrackingPanel, setShowTrackingPanel] = useState(false);
  
  // State for document modals
  const [activeDocumentTab, setActiveDocumentTab] = useState(null);
  
  // State for error popup
  const [errorPopup, setErrorPopup] = useState({
    isVisible: false,
    message: ''
  });

  // State for batch and serial modals
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [currentTrackingItem, setCurrentTrackingItem] = useState(null);

  // ✅ Department feature: screen-config state and constants
  const [screenConfig, setScreenConfig] = useState(null);
  const SCREEN_NAME = 'MRN';

  // Add permissions hook
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  
  // Calculate permissions for MRN, PO and Sales Order
  const canViewMRN = hasFullAccess || hasPermission('MRN_MASTER', 'CanView');
  const canViewSales = hasFullAccess || hasPermission('SALES_ORDER', 'CanView');
  const canViewPO = hasFullAccess || hasPermission('PURCHASE_ORDER', 'CanView');

  // Function to show the error popup
  const showErrorPopup = (message) => {
    setErrorPopup({
      isVisible: true,
      message
    });
  };
  
  // Function to hide the error popup
  const hideErrorPopup = () => {
    setErrorPopup({
      isVisible: false,
      message: ''
    });
  };

    const [columnWidths, setColumnWidths] = useState({
    rowNum: 40,
    itemNo: 200,
    itemName: 200,
    uom: 80,
    rate: 150,
    receivedQty: 200,
    shortExcess: 200,
    location: 200,
    branchCode: 200,
    projectCode: 200,
    poNumber: 200,
    poLine: 200,
    receiptNo: 200,
    department: 200, // Conditional
    trackingType: 200,
    batchSerial: 200,
    attachment: 200,
    remarks: 280
  });
   const resizingRef = useRef(null);
  
  // ✅ Department feature: helpers
  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen;
  };

  const shouldShowDepartmentOnItems = (cfg) => {
    if (!cfg) return false;
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    const showItem = truthy(cfg.ShowOnItem) || truthy(cfg.showOnItem);
    return showScreen && showItem;
  };

  // ✅ Department feature: fetch screen-config
  const fetchScreenConfig = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (e) {}
        const msg = body?.message ? body.message : `Server returned ${res.status}`;
        throw new Error(msg);
      }

      const data = await res.json();
      const cfg = Array.isArray(data) ? (data[0] || null) : data;
      // console.log('Screen info.....', cfg);
      setScreenConfig(cfg);
      return cfg;
    } catch (err) {
      // console.error('fetchScreenConfig error:', err);
      setScreenConfig(null);
      return null;
    }
  };
  
  // Calculate summary values
  const summary = useMemo(() => {
    if (!mrnDetails || !mrnDetails.Items) {
      return {
        totalItems: 0,
        totalReceivedQuantity: "0.00",
        totalAcceptedQuantity: "0.00",
        totalRejectedQuantity: "0.00",
      };
    }
    
    let totalItems = mrnDetails.Items.length;
    let totalReceivedQuantity = 0;
    let totalAcceptedQuantity = 0;
    let totalRejectedQuantity = 0;
    
    mrnDetails.Items.forEach((item) => {
      const receivedQty = parseFloat(item.ReceivedQty || item.receivedQuantity) || 0;
      const acceptedQty = parseFloat(item.AcceptedQty || item.acceptedQuantity) || 0;
      const rejectedQty = parseFloat(item.RejectedQty || item.rejectedQuantity) || 0;
      
      totalReceivedQuantity += receivedQty;
      totalAcceptedQuantity += acceptedQty;
      totalRejectedQuantity += rejectedQty;
    });
    
    return {
      totalItems,
      totalReceivedQuantity: totalReceivedQuantity.toFixed(6),
      totalAcceptedQuantity: totalAcceptedQuantity.toFixed(6),
      totalRejectedQuantity: totalRejectedQuantity.toFixed(6),
    };
  }, [mrnDetails]);

  useEffect(() => {
    if (open) {
      setIsMaximized(false);
      setShowTrackingPanel(false);
      setActiveDocumentTab(null); // Reset document tab state
      // fetchDropdownData();
      fetchScreenConfig();
      
      const mrnNumber = typeof mrnId === 'string' 
        ? mrnId 
        : (mrnId?.MRNNumber || mrnId?.mrnNumber);

       if (mrnId) {
        fetchMRNDetails(mrnId);
        fetchTrackingData(mrnId);
      }
    }
  }, [open, mrnId]);

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  const toggleTrackingPanel = () => {
    const newState = !showTrackingPanel;
    setShowTrackingPanel(newState);
    // Maximize when tracking panel is opened
    if (newState && !isMaximized) {
      setIsMaximized(true);
    }
  };

  // const fetchDropdownData = async () => {
  //   try {
  //     const response = await axios.get(`${API_URL}/mrn/form-data`,{headers: {
  //         'Authorization': `Bearer ${localStorage.getItem('authToken')}`
  //       }});
  //     setDropdownData({
  //       vendors: response.data.vendors || [],
  //       locations: response.data.locations || [],
  //     });
  //   } catch (error) {
  //     // console.error("Error fetching dropdown data:", error);
  //   }
  // };

    const fetchMRNDetails = async (id) => { // Renamed parameter to 'id'
    setLoading(true);
    setError(null);
    try {
      // console.log(`Fetching MRN details for ID: ${id}`);
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API_URL}/mrn/${id}?t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      setMrnDetails(response.data);
    } catch (error) {
      console.error('Error fetching MRN details:', error);
      setError('Failed to load material receipt note details');
      setMrnDetails(null);
    }
    setLoading(false);
  };
  
    const fetchTrackingData = async (id) => { // Renamed parameter to 'id'
    setTrackingLoading(true);
    setTrackingError(null);
    try {
      const response = await axios.get(`${API_URL}/mrn-tracking/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      setTrackingData(response.data);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      setTrackingError('Failed to load tracking information');
    } finally {
      setTrackingLoading(false);
    }
  };

  // In MaterialReceiptViewModal component, add this helper function
  const getRemainingQuantity = (item) => {
    const orderedQty = parseFloat(item.OrderedQty || item.orderedQuantity) || 0;
    const receivedQty = parseFloat(item.ReceivedQty || item.receivedQuantity) || 0;
    return (orderedQty - receivedQty).toFixed(0);
  };

   const handleDownloadAttachment = async (item) => {
    try {
      // UPDATED: Check for IDs instead of Codes
      if (!item.MRNItemID || !mrnDetails.MRNDocumentId) {
        console.error("Missing MRN ID or Item ID");
        alert("Missing required information for download");
        return;
      }
      
      const response = await axios.get(
        // UPDATED URL: Use IDs
        `${API_URL}/mrn/${mrnDetails.MRNDocumentId}/items/${item.MRNItemID}/file`,
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      
      const contentDisposition = response.headers["content-disposition"];
      let filename = item.AttachmentFileName || item.attachmentFileName || "attachment";
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert(`Error downloading file: ${error.message}`);
    }
  };

  // ADD THIS NEW FUNCTION
    const handleDownloadHeaderAttachment = async () => {
    try {
      // UPDATED: Check for ID instead of Number
      if (!mrnDetails.MRNDocumentId) {
        alert("MRN ID not found");
        return;
      }
      
      const response = await axios.get(
        // UPDATED URL: Use ID
        `${API_URL}/mrn/${mrnDetails.MRNDocumentId}/header/file`,
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      
      const contentDisposition = response.headers["content-disposition"];
      let filename = mrnDetails.AttachmentFileName || "attachment";
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading header file:", error);
      alert("Error downloading header file");
    }
  };

  // Helper function to get status label
  const getStatusLabel = (status) => {
    // console.log('Getting status label for:', status, 'Type:', typeof status);
    
    // Handle different possible status formats
    if (status === undefined || status === null) {
      // console.log('Status is undefined or null');
      return '-';
    }
    
    // Let's see the exact value we're getting
    // console.log('Raw status value:', JSON.stringify(status));
    
    // Convert to number if it's a string
    const statusValue = typeof status === 'string' ? parseInt(status, 10) : status;
    
    // If after conversion it's NaN, return '-'
    if (isNaN(statusValue)) {
      // console.log('Status value is NaN after conversion');
      return '-';
    }
    
    // Find the matching status option
    const statusOption = STATUS_OPTIONS.find(option => option.value === statusValue);
    if (statusOption) {
      // console.log('Found status option:', statusOption.label);
      return statusOption.label;
    } else {
      // Fallback to direct mapping
      const label = statusValue === 1 ? 'Active' : 'Inactive';
      // console.log('Using fallback mapping:', label);
      return label;
    }
  };

  // Helper function to get supplier name
  const getSupplierName = (mrn) => {
    // Try different possible field names for supplier name
    return mrn.VendorName || mrn.vendor_name || mrn.vendorName || mrn.supplierName || '-';
  };

  // Helper function to get location name
  const getLocationName = (warehouseCode) => {
    if (!warehouseCode) return '-';
    const location = dropdownData.locations.find(loc => loc.LocationCode === warehouseCode);
    return location ? location.LocationName : warehouseCode;
  };

  // Helper function to get tracking type label
  const getTrackingTypeLabel = (trackingType) => {
    if (trackingType === undefined || trackingType === null) return 'None';
    const type = parseInt(trackingType);
    switch(type) {
      case 1: return 'Batch';
      case 2: return 'Serial';
      default: return 'None';
    }
  };

  // Helper function to get tracking type style
  const getTrackingTypeStyle = (trackingType) => {
    if (trackingType === undefined || trackingType === null) return 'bg-gray-100 text-gray-800';
    const type = parseInt(trackingType);
    switch(type) {
      case 1: return 'bg-blue-100 text-blue-800';
      case 2: return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get batch info summary
  const getBatchInfoSummary = (item) => {
    if (!item.Batches || !Array.isArray(item.Batches) || item.Batches.length === 0) {
      return 'No batch data';
    }
    
    const count = item.Batches.length;
    const totalQty = item.Batches.reduce((sum, batch) => sum + (parseFloat(batch.Quantity || batch.quantity) || 0), 0);
    
    return `${count} batch${count !== 1 ? 'es' : ''} (Total: ${totalQty})`;
  };

  // Helper function to get serial info summary
  const getSerialInfoSummary = (item) => {
    if (!item.SerialNumbers || !Array.isArray(item.SerialNumbers) || item.SerialNumbers.length === 0) {
      return 'No serial data';
    }
    
    return `${item.SerialNumbers.length} serial number${item.SerialNumbers.length !== 1 ? 's' : ''}`;
  };

  // Handle view batch details
  const handleViewBatchDetails = (item) => {
    setCurrentTrackingItem(item);
    setShowBatchModal(true);
  };

  // Handle view serial details
  const handleViewSerialDetails = (item) => {
    setCurrentTrackingItem(item);
    setShowSerialModal(true);
  };



    // --- RESIZABLE HELPER COMPONENT ---
  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th
      style={{ width: width, position: 'relative', userSelect: 'none' }}
      className={`p-2 bg-[#BDDDFC] text-black text-sm font-semibold ${
        align === 'center' ? 'text-center' : 'text-left'
      }`}
    >
      {children}
      <div
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-blue-400"
        onMouseDown={(e) => {
          e.preventDefault();
          onResize(e.clientX);
        }}
      />
    </th>
  );

  // --- RESIZABLE HANDLERS ---
  const startResize = (columnKey, startX) => {
    resizingRef.current = {
      columnKey,
      startX,
      startWidth: columnWidths[columnKey],
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;
    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;
    const newWidth = Math.max(40, startWidth + delta); // Min width 40px

    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: newWidth,
    }));
  };

  const stopResize = () => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
  };

  // Enhanced Tree Structure Component for MRN Tracking with Sales -> PO -> MRN flow
  const TrackingTree = ({ data }) => {
    // Move useMemo to top level to avoid conditional hook error
    const reorganizedData = useMemo(() => {
      if (!data) {
        return {
          salesOrders: [],
          purchaseOrders: [],
          mrn: null
        };
      }

      const { mrn, chains } = data;
      
      // Collect all unique sales orders and map them to their POs
      const salesOrdersMap = new Map();
      const purchaseOrdersSet = new Set();
      
      chains.forEach(chain => {
        const poNumber = chain.purchaseOrder.purchaseOrderNumber;
        purchaseOrdersSet.add(poNumber);
        
        if (chain.salesOrders && chain.salesOrders.length > 0) {
          chain.salesOrders.forEach(so => {
            if (!salesOrdersMap.has(so.salesOrderNumber)) {
              salesOrdersMap.set(so.salesOrderNumber, {
                ...so,
                linkedPOs: []
              });
            }
            salesOrdersMap.get(so.salesOrderNumber).linkedPOs.push({
              purchaseOrderNumber: poNumber,
              purchaseOrderDate: chain.purchaseOrder.purchaseOrderDate,
              status: chain.purchaseOrder.status
            });
          });
        }
      });
      
      // Convert maps to arrays
      const salesOrders = Array.from(salesOrdersMap.values());
      const purchaseOrders = Array.from(purchaseOrdersSet).map(poNumber => {
        const chain = chains.find(c => c.purchaseOrder.purchaseOrderNumber === poNumber);
        return chain.purchaseOrder;
      });
      
      return {
        salesOrders,
        purchaseOrders,
        mrn
      };
    }, [data]);
    
    const StatusBadge = ({ status }) => {
      const statusStyle = status === 'Completed' || status === 'Active' 
        ? 'bg-green-100 text-green-800' 
        : 'bg-yellow-100 text-yellow-800';
        
      return (
        <span className={`text-xs px-2 py-1 rounded-full ${statusStyle}`}>
          {status}
        </span>
      );
    };
    
    // Enhanced Document Node Component with click handling
    const DocumentNode = ({ 
      type, 
      number, 
      date, 
      status, 
      isPending = false,
      isCenter = false,
      onClick
    }) => {
      const getIcon = () => {
        switch(type) {
          case 'sales':
            return <SalesOrderIcon className="text-white" />;
          case 'purchase':
            return <PurchaseOrderIcon className="text-white" />;
          case 'mrn':
            return <MRNIcon className={isPending ? "text-gray-400" : "text-white"} />;
          default:
            return null;
        }
      };
      
      const getColorClass = () => {
        if (isPending) return 'bg-gray-200';
        switch(type) {
          case 'sales':
            return 'bg-purple-500' ;
          case 'purchase':
            return 'bg-blue-500';
          case 'mrn':
            return 'bg-green-500';
          default:
            return 'bg-gray-500';
        }
      };
      
      const isClickable = (type === 'sales' && canViewSales && !isPending) || 
                         (type === 'mrn' && canViewMRN && !isPending) || 
                         (type === 'purchase' && canViewPO && !isPending);
      
      const handleClick = () => {
        if (isClickable && onClick) {
          onClick(type, number);
        } else if (!isClickable && !isPending) {
          if (type === 'sales') {
            showErrorPopup("You don't have permission to view Sales Orders");
          } else if (type === 'purchase') {
            showErrorPopup("You don't have permission to view Purchase Orders");
          }
        }
      };
      
      return (
        <div 
          className={`flex flex-col items-center mt-4 cursor-pointer hover:opacity-80 transition-opacity`}
          onClick={handleClick}
          title={isClickable ? `View ${type === 'sales' ? 'Sales Order' : type === 'purchase' ? 'Purchase Order' : 'MRN'} in new tab` : 
                isPending ? 'Not created yet' : 'No permission to view'}
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClass()}`}>
            {getIcon()}
          </div>
          <div className={`mt-2 text-center ${isPending ? 'text-gray-400' : 'text-gray-800'}`}>
            <div className="font-medium text-sm">
              {type === 'sales' && 'Sales Order'}
              {type === 'purchase' && 'Purchase Order'}
              {type === 'mrn' && 'MRN'}
            </div>
            <div className="font-semibold">{number}</div>
            {date && !isPending && (
              <div className="text-xs text-gray-500 mt-1">
                {dayjs(date).format('MMM DD, YYYY')}
              </div>
            )}
            {status && !isPending && <StatusBadge status={status} className="mt-2" />}
            {isPending && (
              <div className="text-xs text-gray-400 italic mt-2">Not created yet</div>
            )}
            {!isClickable && !isPending && (
              <div className="text-xs text-red-500 italic mt-1">No permission</div>
            )}
          </div>
        </div>
      );
    };
    
    // Connector from Sales Orders to Purchase Orders
    const SalesToPosConnector = ({ salesCount, posCount }) => {
      return (
        <div className="relative w-full h-16 flex items-center justify-center">
          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Vertical lines from each sales order */}
            {[...Array(salesCount)].map((_, i) => {
              const x = (i + 1) * (100 / (salesCount + 1));
              return (
                <line 
                  key={`sales-line-${i}`}
                  x1={`${x}%`} 
                  y1="0" 
                  x2={`${x}%`} 
                  y2="30" 
                  stroke="#4B5563" 
                  strokeWidth="0.3"
                />
              );
            })}
            
            {/* Horizontal connecting line */}
            <line 
              x1={`${100 / (salesCount + 1)}%`} 
              y1="30" 
              x2={`${salesCount * 100 / (salesCount + 1)}%`} 
              y2="30" 
              stroke="#4B5563" 
              strokeWidth="1"
            />
            
            {/* Vertical line to POs level */}
            <line 
              x1="50%" 
              y1="30" 
              x2="50%" 
              y2="70" 
              stroke="#4B5563" 
              strokeWidth="0.3"
            />
            
            {/* Arrowhead pointing to POs */}
            <polygon
              points="50,70 47,63 53,63" 
              fill="#4B5563" 
            />
          </svg>
        </div>
      );
    };
    
    // Connector from Purchase Orders to MRN
    const PosToMrnConnector = ({ posCount }) => {
      return (
        <div className="relative w-full h-16 flex items-center justify-center">
          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Vertical lines from each PO */}
            {[...Array(posCount)].map((_, i) => {
              const x = (i + 1) * (100 / (posCount + 1));
              return (
                <React.Fragment key={`po-line-${i}`}>
                  <line 
                    x1={`${x}%`} 
                    y1="0" 
                    x2={`${x}%`} 
                    y2="30" 
                    stroke="#4B5563" 
                    strokeWidth="0.3"
                  />
                  {/* Arrowhead for each PO */}
                  <polygon 
                    points={`${x},30 ${parseFloat(x) - 1.5},23 ${parseFloat(x) + 1.5},23`} 
                    fill="#4B5563" 
                  />
                </React.Fragment>
              );
            })}
            
            {/* Horizontal connecting line */}
            <line 
              x1={`${100 / (posCount + 1)}%`} 
              y1="30" 
              x2={`${posCount * 100 / (posCount + 1)}%`} 
              y2="30" 
              stroke="#4B5563" 
              strokeWidth="1"
            />
            
            {/* Vertical line to MRN */}
            <line 
              x1="50%" 
              y1="30" 
              x2="50%" 
              y2="100" 
              stroke="#4B5563" 
              strokeWidth="0.3"
            />
            
            {/* Arrowhead pointing to MRN */}
            <polygon
              points="50,100 47,93 53,93" 
              fill="#4B5563" 
            />
          </svg>
        </div>
      );
    };
    
    // Handle document click
    const handleDocumentClick = (docType, docNumber) => {
      // console.log(`Document clicked: ${docType}, Number: ${docNumber}`);
      if (docType === 'sales') {
        const newTab = {
          type: 'sales',
          SalesOrderNumber: docNumber,
          salesOrderNumber: docNumber,
        };
        // console.log('Setting activeDocumentTab to:', newTab);
        setActiveDocumentTab(newTab);
      } else if (docType === 'purchase') {
        setActiveDocumentTab({
          type: 'purchase',
          PurchaseOrderNumber: docNumber,
          purchaseOrderNumber: docNumber,
        });
      }
    };
    
    return (
      <div className="bg-white h-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-[#384959]">Document Tracking</h3>
          <button
            onClick={() => setShowTrackingPanel(false)}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
            title="Hide Panel"
          >
            <HidePanelIcon size={20} />
          </button>
        </div>
        
        {trackingLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading tracking information...</p>
            </div>
          </div>
        ) : trackingError ? (
          <div className="bg-yellow-50 text-yellow-700 p-4 rounded-lg mb-6 border border-yellow-100">
            Tracking Error: {trackingError}
          </div>
        ) : reorganizedData.mrn ? (
          <div className="flex flex-col items-center">
            {/* Sales Orders Section */}
            {reorganizedData.salesOrders.length > 0 ? (
              <>
                <div className="flex justify-center space-x-8 mb-2">
                  {reorganizedData.salesOrders.map(so => (
                    <DocumentNode 
                      key={so.salesOrderNumber}
                      type="sales"
                      number={so.salesOrderNumber}
                      date={so.salesOrderDate}
                      onClick={handleDocumentClick}
                    />
                  ))}
                </div>
                
                {/* Flowchart connector from Sales Orders to Purchase Orders */}
                <SalesToPosConnector 
                  salesCount={reorganizedData.salesOrders.length} 
                  posCount={reorganizedData.purchaseOrders.length} 
                />
              </>
            ) : (
              <div className="flex justify-center">
                <DocumentNode 
                  type="sales"
                  number="Sales Order"
                  isPending={true}
                />
              </div>
            )}
            
            {/* Purchase Orders Section */}
            {reorganizedData.purchaseOrders.length > 0 ? (
              <>
                <div className="flex justify-center space-x-8 mb-2">
                  {reorganizedData.purchaseOrders.map(po => (
                    <DocumentNode 
                      key={po.purchaseOrderNumber}
                      type="purchase"
                      number={po.purchaseOrderNumber}
                      date={po.purchaseOrderDate}
                      status={po.status}
                      onClick={handleDocumentClick}
                    />
                  ))}
                </div>
                
                {/* Flowchart connector from Purchase Orders to MRN */}
                <PosToMrnConnector posCount={reorganizedData.purchaseOrders.length} />
              </>
            ) : (
              <div className="flex justify-center">
                <DocumentNode 
                  type="purchase"
                  number="Purchase Order"
                  isPending={true}
                />
              </div>
            )}
            
            {/* MRN Section */}
            <div className="flex justify-center">
              <DocumentNode 
                type="mrn"
                number={reorganizedData.mrn.mrnNumber}
                date={reorganizedData.mrn.mrnDate}
                status={reorganizedData.mrn.status}
                isCenter={true}
              />
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            No tracking data available
          </div>
        )}
      </div>
    );
  };

  if (!open || !mrnId) return null;

  const mrn = mrnDetails || mrnId;
  const mrnItems = mrnDetails?.Items || [];
  
  // Get status value for debugging
  const statusValue = mrnId.Status !== undefined ? mrnId.Status : (mrnId.status !== undefined ? mrnId.status : 'undefined');
  // console.log('Rendering with status value:', statusValue);

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${
        open ? "opacity-100 visible" : "opacity-0 invisible"
      }`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out forwards;
          }
            .resizable-table {
            table-layout: fixed; /* Important for width control */
            width: 100%;
            border-collapse: collapse;
          }
          
          .resizable-table th, .resizable-table td {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        `}
      </style>
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      <div
        className={`bg-white shadow-2xl flex flex-col transition-all duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : showTrackingPanel ? 'w-[90%] max-w-7xl' : 'w-full max-w-6xl'} h-full rounded-none`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">
              Material Receipt Note Details
            </h2>
            <div className="flex gap-2">
              {/* <button 
                className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                  showTrackingPanel 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                }`}
                onClick={toggleTrackingPanel}
                title="Track Document"
              >
                <TrackDocumentIcon size={18} />
                <span>Track Document</span>
              </button> */}
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1"
                onClick={onClose}
                title="Close"
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Main Content Area with Sidebar Layout */}
        <div className="flex-grow overflow-hidden flex">
          {/* Main MRN Details Content */}
          <div className={`flex-grow overflow-y-auto hide-scrollbar px-8 ${showTrackingPanel ? 'pr-4' : ''}`}>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading material receipt note details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
                Error: {error}
              </div>
            ) : (
              <div className="space-y-6 pb-6">
                {/* MRN Header */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">
                    Material Receipt Note Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        MRN Number
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.MRNNumber || mrn.mrnNumber || '-'}
                      </div>
                    </div>
                    {/* <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        MRN Date
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.MRNDate ? dayjs(mrn.MRNDate).format('YYYY-MM-DD') : '-'}
                      </div>
                    </div> */}
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Vendor Name *
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {getSupplierName(mrn)}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Location Code *
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                         {mrn.LocationCode  || '-'}
                      </div>
                    </div>
                     <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Branch
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.BranchCode || '-'} {mrn.BranchName}
                      </div>
                    </div>
                     <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Project Code
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.ProjectCode || '-'} 
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Received By *
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.ReceivedBy || mrn.receivedBy || '-'}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Vendor Invoice No *
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.VendorInvoiceNo || mrn.vendorInvoiceNo || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Invoice Received Date *
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {formatDate(mrn.InvoiceReceivedDate)}
                        {/* {mrn.InvoiceReceivedDate ? dayjs(mrn.InvoiceReceivedDate).format('YYYY-MM-DD') : '-'} */}
                      </div>
                    </div>
                     <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Bill Date
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {formatDate(mrn.BillDate)}
                      </div>
                    </div>

                    

                    {/* ✅ Department in header (conditional) */}
                    {/* { (shouldShowDepartmentOnHeader(screenConfig || mrn?.screenConfig)) && (
                      <div className="flex flex-col gap-2 md:col-span-2">
                        <label className="block text-sm font-medium text-[#64748b]">Department</label>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                          {` ${mrn.DepartmentCode || '-'}`}
                        </div>
                      </div>
                    )} */}

                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Delivery Note Number
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.DeliveryNoteNumber || mrn.deliveryNoteNumber || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Challan Number
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.ChallanNumber || mrn.challanNumber || '-'}
                      </div>
                    </div>
                     <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Eway Bill Number
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.EWayBillNumber}
                      </div>
                    </div>
                     <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Vehicle Number
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.VehicleNumber}
                      </div>
                    </div>
                     <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Transporter
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.Transporter}
                      </div>
                    </div>
                     <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        GR Number
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.GRNumber}
                      </div>
                    </div>
                    {/* <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Pending Bill Number
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {mrn.PendingBillNumber || mrn.pendingBillNumber || '-'}
                      </div>
                    </div> */}
                   
                    
                    {/* Approval/BC Status - ADD THIS NEW FIELD */}
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    Approval/BC Status
  </label>
  <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
    {(mrn.SendToBC === 1 || mrn.SendToBC === true) ? (
      <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
        Data Successfully Sent to BC
      </span>
    ) : (mrn.ValidateData === 1 || mrn.ValidateData === true) ? (
      <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-200">
        Validated (Ready to Send BC)
      </span>
    ) : mrn.ApprovalStatus === 'PENDING' ? (
      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
        Pending Approval (Level {mrn.CurrentLevel || 1})
      </span>
    ) : mrn.ApprovalStatus === 'REJECTED' ? (
      <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
        Rejected
      </span>
    ) : mrn.ApprovalStatus === 'APPROVED' ? (
      <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
        Approved (Ready to Validate)
      </span>
    ) : (
      <span className="text-xs text-gray-500 italic">Open</span>
    )}
  </div>
</div>


{/* ADD THIS BLOCK - Header Attachment Field */}
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    Attachment
  </label>
  <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] flex items-center justify-between min-h-[38px]">
    {mrn.AttachmentFileName ? (
      <>
        <span className="truncate text-sm text-gray-800 uppercase">
          {mrn.AttachmentFileName}
        </span>
        <button
          type="button"
          onClick={handleDownloadHeaderAttachment}
          className="text-green-600 hover:text-green-800 ml-2 flex-shrink-0"
          title="Download Attachment"
        >
          <FaDownload size={14} />
        </button>
      </>
    ) : (
      <span className="text-gray-400 text-sm italic">No attachment</span>
    )}
  </div>
</div>


                    <div className="flex flex-col gap-2 md:col-span-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Remarks
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] min-h-[60px] uppercase">
                        {mrn.Remarks || mrn.remarks || '-'}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* MRN Items */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-[#384959]">
                      Material Receipt Items
                    </h3>
                  </div>
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="resizable-table border border-[#E2E8F0]">
                      <thead>
                        <tr className="bg-[#BDDDFC] text-[#384959]">
                          <ResizableTh 
                           width={columnWidths.rowNum}
                           onResize={(x) => startResize('rowNum', x)} 
                           align="center">#
                          </ResizableTh>
                          <ResizableTh width={columnWidths.itemNo} onResize={(x) => startResize('itemNo', x)}>Item No</ResizableTh>
                          <ResizableTh width={columnWidths.itemName} onResize={(x) => startResize('itemName', x)}>Item Name</ResizableTh>
                          <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>UOM</ResizableTh>
                          <ResizableTh width={columnWidths.rate} onResize={(x) => startResize('rate', x)}>Rate</ResizableTh>
                          {/* <th className="p-2">Ordered Quantity</th> */}
                          
                          <ResizableTh width={columnWidths.receivedQty} onResize={(x) => startResize('receivedQty', x)}>Received Quantity</ResizableTh>
                          <ResizableTh width={columnWidths.shortExcess} onResize={(x) => startResize('shortExcess', x)}>Short/Excess Qty</ResizableTh>
        
                          {/* <th className="p-2">UOM</th> */}
                          {/* <th className="p-2">Accepted Quantity</th>
                          <th className="p-2">Rejected Quantity</th> */}
                          
                          <ResizableTh width={columnWidths.location} onResize={(x) => startResize('location', x)}>Location</ResizableTh>
                          <ResizableTh width={columnWidths.branchCode} onResize={(x) => startResize('branchCode', x)}>Branch Code</ResizableTh>
                          <ResizableTh width={columnWidths.projectCode} onResize={(x) => startResize('projectCode', x)}>Project Code</ResizableTh>
                          <ResizableTh width={columnWidths.poNumber} onResize={(x) => startResize('poNumber', x)}>PO Number</ResizableTh>
                          <ResizableTh width={columnWidths.poLine} onResize={(x) => startResize('poLine', x)}>PoLineNumber</ResizableTh>
                          <ResizableTh width={columnWidths.receiptNo} onResize={(x) => startResize('receiptNo', x)}>Receipt No</ResizableTh>
        
                          {/* <th className="p-2">Item Code</th> */}

                          {/* ✅ Conditional header for Department column on items */}
                          {shouldShowDepartmentOnItems(screenConfig || mrn?.screenConfig) && (
                            <th className="p-2">Department</th>
                          )}

                          {/* Tracking columns */}
                          <ResizableTh width={columnWidths.trackingType} onResize={(x) => startResize('trackingType', x)}>Tracking Type</ResizableTh>
                          <ResizableTh width={columnWidths.batchSerial} onResize={(x) => startResize('batchSerial', x)}>Batch/Serial Info</ResizableTh>
                          {/* <ResizableTh width={columnWidths.attachment} onResize={(x) => startResize('attachment', x)}>Attachment</ResizableTh> */}
                          <ResizableTh width={columnWidths.remarks} onResize={(x) => startResize('remarks', x)}>Remarks</ResizableTh>
     
                        </tr>
                      </thead>
                      <tbody>
                        {mrnItems.length > 0 ? (
                          mrnItems.map((item, idx) => (
                            <tr key={idx} className="border-t border-[#E2E8F0]">
                               <td className="p-2 text-center text-[#384959]" style={{ width: columnWidths.rowNum }}>{idx + 1}</td>
        
                             <td className="p-2 uppercase" style={{ width: columnWidths.itemNo }}>
                                
                                  {item.ProductCode ||'-'}
                                
                              </td>
                              <td className="p-2 uppercase" style={{ width: columnWidths.itemName }}>
                                  {item.ProductName || item.productName || '-'}
                              
                              </td>
                              <td className="p-2 uppercase" style={{ width: columnWidths.uom }}>
                                  {item.UOM || item.uom || '-'}
                              </td>
                              <td className="p-2" style={{ width: columnWidths.rate }}>
                                  {item.UnitPrice }
                                
                              </td>

                              {/* <td className="p-2">
                                <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                  {item.OrderedQty || item.orderedQuantity || '-'}
                                </div>
                              </td> */}
                              
                              <td className="p-2" style={{ width: columnWidths.receivedQty }}>
                                  {item.ReceivedQty || item.receivedQuantity || '-'}
                                
                              </td>
                              <td className="p-2" style={{ width: columnWidths.shortExcess }}>
                                  {item.ShortageQty || '-'}
                              </td>
                              {/* <td className="p-2">
                                <div className="w-20 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                  {item.UOM || item.uom || '-'}
                                </div>
                              </td> */}
                              {/* <td className="p-2">
                                <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                  {item.AcceptedQty || item.acceptedQuantity || '-'}
                                </div>
                              </td>
                              <td className="p-2">
                                <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                  {item.RejectedQty || item.rejectedQuantity || '-'}
                                </div>
                              </td> */}
                              <td className="p-2 uppercase" style={{ width: columnWidths.location }}>
                                  {getLocationName(item.WarehouseCode || item.warehouseCode)}
                               
                              </td>
                              <td className="p-2 uppercase" style={{ width: columnWidths.branchCode }}>
                                  {item.BranchCode || '-'}
                               
                              </td>
                               <td className="p-2 uppercase" style={{ width: columnWidths.projectCode }}>
                                  {item.ProjectCode || '-'}
                              </td>

                              <td className="p-2" style={{ width: columnWidths.poNumber }}>
                                  {item.PurchaseOrderNumber || item.purchaseOrderNumber || '-'}
                              </td>
                              <td className="p-2" style={{ width: columnWidths.poLine }}>
                                  {item.POLineNumber  || '-'}
                               
                              </td>
                              <td className="p-2" style={{ width: columnWidths.receiptNo }}>
                                  {item.BCReceiptNo  || '-'}
                              </td>

                              {/* <td className="p-2">
                                <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                  {item.ItemCode || item.itemCode || '-'}
                                </div>
                              </td> */}

                              {/* ✅ Conditional cell for Department per item */}
                              {shouldShowDepartmentOnItems(screenConfig || mrn?.screenConfig) && (
                                <td className="p-2">
                                  <div className="w-40 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {`$${item.DepartmentCode || '-'}`}
                                  </div>
                                </td>
                              )}

                              {/* Tracking Type */}
                               <td className="p-2" style={{ width: columnWidths.trackingType }}>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTrackingTypeStyle(item.ProductTrackingType || item.productTrackingType)}`}>
                                      {getTrackingTypeLabel(item.ProductTrackingType || item.productTrackingType)}
                                  </span>
                              </td>

                              {/* Batch/Serial Info */}
                              <td className="p-2" style={{ width: columnWidths.batchSerial }}>
                                <div className="flex items-center">
                                  <div className="text-xs max-w-xs">
                                    {parseInt(item.ProductTrackingType || item.productTrackingType) === 1 ? (
                                      <div>
                                        <div className="font-medium">Batch Info:</div>
                                        <div className="text-gray-600">{getBatchInfoSummary(item)}</div>
                                      </div>
                                    ) : parseInt(item.ProductTrackingType || item.productTrackingType) === 2 ? (
                                      <div>
                                        <div className="font-medium">Serial Info:</div>
                                        <div className="text-gray-600">{getSerialInfoSummary(item)}</div>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">No tracking</span>
                                    )}
                                  </div>
                                  {(parseInt(item.ProductTrackingType || item.productTrackingType) === 1 || 
                                    parseInt(item.ProductTrackingType || item.productTrackingType) === 2) && (
                                    <button
                                      type="button"
                                      className="ml-2 text-blue-600 hover:text-blue-800"
                                      onClick={() => {
                                        if (parseInt(item.ProductTrackingType || item.productTrackingType) === 1) {
                                          handleViewBatchDetails(item);
                                        } else {
                                          handleViewSerialDetails(item);
                                        }
                                      }}
                                      title="View details"
                                    >
                                      <FaEye size={14} />
                                    </button>
                                  )}
                                </div>
                              </td>

                              {/* <td className="p-2" style={{ width: columnWidths.attachment }}>
                                <div className="flex flex-col items-center">
                                  {item.AttachmentFileName || item.attachmentFileName ? (
                                    <>
                                      <div className="text-xs truncate max-w-[100px] mb-1">
                                        {item.AttachmentFileName || item.attachmentFileName}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleDownloadAttachment(item)}
                                        className="text-green-600 hover:text-green-800"
                                        title="Download"
                                      >
                                        <FaDownload size={12} />
                                      </button>
                                    </>
                                  ) : (
                                    <span className="text-gray-400">No attachment</span>
                                  )}
                                </div>
                              </td> */}
                              <td className="p-2 uppercase" style={{ width: columnWidths.remarks }}>
                                <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                  {item.Remarks || item.remarks || '-'}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={shouldShowDepartmentOnItems(screenConfig || mrn?.screenConfig) ? 16 : 14}
                              className="text-center text-gray-500 py-4"
                            >
                              No items found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Total Summary Section */}
                  <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                    <h4 className="text-md font-semibold text-[#384959] mb-4">
                      Total Summary
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Items</span>
                        <span className="text-base font-semibold">
                          {summary.totalItems}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">
                          Total Received Quantity
                        </span>
                        <span className="text-base font-semibold">
                          {summary.totalReceivedQuantity}
                        </span>
                      </div>
                      {/* <div className="flex flex-col">
                        <span className="text-sm text-gray-500">
                          Total Accepted Quantity
                        </span>
                        <span className="text-base font-semibold">
                          {summary.totalAcceptedQuantity}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">
                          Total Rejected Quantity
                        </span>
                        <span className="text-base font-semibold text-red-600">
                          {summary.totalRejectedQuantity}
                        </span>
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Tracking Sidebar */}
          {showTrackingPanel && (
            <div className="border-l overflow-y-auto hide-scrollbar bg-white rounded-xl p-6 border border-gray-200 h-full" style={{width : "50vw"}}>
              <TrackingTree data={trackingData} />
            </div>
          )}
        </div>
        
        {/* Fixed Footer with Button */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      
      {/* Batch Info Modal */}
      <BatchInfoModal
        isOpen={showBatchModal}
        onClose={() => {
          setShowBatchModal(false);
          setCurrentTrackingItem(null);
        }}
        batches={currentTrackingItem?.Batches || currentTrackingItem?.batches || []}
        productName={currentTrackingItem?.ProductName || currentTrackingItem?.productName || ""}
      />
      
      {/* Serial Info Modal */}
      <SerialInfoModal
        isOpen={showSerialModal}
        onClose={() => {
          setShowSerialModal(false);
          setCurrentTrackingItem(null);
        }}
        serialNumbers={currentTrackingItem?.SerialNumbers || currentTrackingItem?.serialNumbers || []}
        productName={currentTrackingItem?.ProductName || currentTrackingItem?.productName || ""}
        locationCode={currentTrackingItem?.WarehouseCode || currentTrackingItem?.warehouseCode || ""}
      />
      
      {/* Document Tab Modals */}
      {activeDocumentTab && activeDocumentTab.type === 'sales' && (
        <SalesOrderViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          soId={activeDocumentTab} // Pass the entire object with both properties
        />
      )}
      
      {activeDocumentTab && activeDocumentTab.type === 'purchase' && (
        <PurchaseOrderViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          poId={activeDocumentTab} // Pass the entire object with both properties
        />
      )}
      
      {/* Modern Error Popup */}
      <ErrorPopup 
        message={errorPopup.message} 
        isVisible={errorPopup.isVisible} 
        onClose={hideErrorPopup} 
      />
    </div>
  );
};

export default MaterialReceiptViewModal;